<template>
  <AdminLayout>
    <Head title="Products" />

    <template #pageTitle> Products </template>

    <template #pageTitleWidgets>
      <DataGridControlBar />
    </template>

    <template #headingOptions>
      <button
        @click="openCreate"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-5 w-5"
          aria-hidden="true"
        />
        Add Product
      </button>
    </template>

    <DataGrid
      name="products_data_table"
      :endpoint="route('api.tables.products.index')"
      rowId="id"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
    />

    <template #modals>
      <SmallModal
        :is-open="showingCreateModal"
        @close="showingCreateModal = false"
      >
        <template #header> Create Product </template>

        <div class="mt-4">
          <CreateForm />
        </div>
      </SmallModal>
    </template>
  </AdminLayout>
</template>

<script setup>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
useInitGridState()
</script>

<script>
import { Popover, PopoverButton, PopoverPanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
  PlusSmIcon as PlusSmIconSolid,
  DotsHorizontalIcon,
  ChevronDownIcon,
  ArrowCircleRightIcon
} from '@heroicons/vue/solid'
import { PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import { Head, Link } from '@inertiajs/vue3'
import BasicBadge from '@/Components/Badges/BasicBadge.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import SelectMenu from '@/Components/Menus/SelectMenu.vue'
import DispenseForm from '@/Partials/Products/DispenseForm'
import CreateForm from '@/Partials/Products/CreateForm'
import formatters from '@/Mixins/formatters'
import isUrlAware from '@/Mixins/isUrlAware'
import _date from '@/Filters/Date'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import StatusCell from '@/Components/DataGrid/Cells/StatusCell.vue'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import PublishStatusCell from '@/Components/DataGrid/Cells/PublishStatusCell.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'

export default {
  mixins: [formatters, isUrlAware],

  components: {
    Head,
    Link,
    DotsHorizontalIcon,
    ChevronDownIcon,
    ArrowCircleRightIcon,
    Popover,
    PopoverButton,
    PopoverPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    BasicBadge,
    SmallModal,
    SelectMenu,
    DispenseForm,
    CreateForm,
    PlusSmIconOutline,
    PlusSmIconSolid,
    DataGrid,
    StatusCell,
    LinkCell,
    DateCell,
    DollarCell,
    PublishStatusCell,
    DataGridControlBar
  },

  props: {
    categories: {
      type: Array,
      required: true
    },
    publishStatuses: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      showingCreateModal: false,
      form: {},
      columns: [
        {
          headerName: 'Product',
          field: 'name',
          cellRenderer: 'LinkCell',
          cellRendererParams: (params) => ({
            url: `/products/${params.data.id}`,
            display: params.value
          }),
          filter: 'agTextColumnFilter',
          flex: 1,
          minWidth: 320
        },
        {
          headerName: 'SKU',
          field: 'sku',
          filter: 'agTextColumnFilter'
        },
        {
          headerName: 'Total Qty',
          field: 'quantity_total',
          sortable: false,
          width: 120
        },
        {
          headerName: 'Available Qty',
          field: 'quantity_available',
          sortable: false,
          width: 120
        },
        {
          headerName: 'Price',
          field: 'unit_price',
          cellRenderer: 'DollarCell',
          filter: 'agNumberColumnFilter'
        },
        {
          headerName: 'Category',
          field: 'category_name',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: this.categories
          },
          suppressHeaderMenuButton: true
        },
        {
          headerName: 'Batch Count',
          field: 'batch_count',
          filter: 'agNumberColumnFilter',
          width: 120
        },
        {
          headerName: 'Status',
          field: 'publish_status',
          cellRenderer: 'PublishStatusCell',
          sortable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: this.publishStatuses
          }
        }
      ],
      dataGridState: null
    }
  },

  computed: {
    showProductRoute() {
      // A better argument name here would be 'package' but thats a reserved word in javascript
      return (item) => {
        return route('products.edit', { product: item.id })
      }
    }
  },

  methods: {
    _date,
    openCreate() {
      this.showingCreateModal = true
    }
  }
}
</script>
